/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from 'react-i18next';
import { useAuth } from "../../../context/AuthContext";
import picturePlaceHolder from "../../../assets/images/picture_placeholder.svg";
import { updateEOBusinessProfile } from "../../../firebaseFunctions/updateEOBusinessProfile/updateEOBusinessProfile";
import ModalComponent from "../../MPS/EventRegistration/EventsModal";
import PopUpModal from "../../PopUpModal/PopUpModal";
import ModalLoadingState from "../../PopUpModal/ModalLoadingState";
import { CheckCircleIcon as CheckCircleIconSolid } from "@heroicons/react/solid";
import alert from "../../../assets/images/alert-circle.svg";
import { validatePhoneNumber } from "../../../utils/validations";
import { Camera, Pencil, Trash2, Building2, Phone, Mail, User, FileText, ShieldAlert } from "lucide-react";

const InfoRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3 py-3 border-b border-gray-100 last:border-0">
    <div className="flex-shrink-0 w-7 h-7 rounded-md bg-gray-100 flex items-center justify-center mt-0.5">
      <Icon className="w-3.5 h-3.5 text-gray-500" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-xs text-gray-400 mb-0.5">{label}</p>
      <p className="text-sm font-medium text-gray-900 truncate">
        {value || <span className="text-gray-400 font-normal">—</span>}
      </p>
    </div>
  </div>
);

const FieldInput = ({ label, name, value, placeholder, type = "text", error, helperText, onChange, disabled }) => (
  <div>
    <label className="block text-xs font-medium text-gray-700 mb-1.5">{label}</label>
    <input
      type={type}
      value={value || ""}
      onChange={e => onChange(name, e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      className={`w-full px-3 py-2.5 text-sm border rounded-lg bg-white text-gray-900 placeholder-gray-400
        focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition
        ${error ? "border-red-400 focus:ring-red-500" : "border-gray-300"}
        ${disabled ? "opacity-50 cursor-not-allowed bg-gray-50" : ""}`}
    />
    {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    {helperText && !error && <p className="mt-1 text-xs text-gray-500">{helperText}</p>}
  </div>
);

const ProfileContent = ({
  userDetails,
  reload,
  isLoading,
  setIsLoading,
  isModalOpen,
  setIsModalOpen,
  popUpModalType,
  setPopUpModalType,
  userSignInMethod
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const fileInputRef = useRef(null);

  const buildInitialState = (details) => ({
    companyLogo: details?.companyLogo || "",
    businessName: details?.businessName || "",
    fullName: details?.fullName || "",
    userEmail: details?.userEmail || "",
    recoveryEmail: details?.recoveryEmail || "",
    contactNumber: details?.contactNumber || "",
    businessDescription: details?.businessDescription || "",
    businessType: details?.businessType || "",
  });

  const [formState, setFormState] = useState({});
  const [errors, setErrors] = useState({});
  const [isEdit, setIsEdit] = useState(false);

  useEffect(() => {
    if (userDetails) setFormState(buildInitialState(userDetails));
  }, [userDetails]);

  const handleFieldChange = (field, value) => {
    if (field === "userEmail" && userSignInMethod === "Google") return;
    if (field === "contactNumber") {
      setErrors(prev => ({
        ...prev,
        contactNumber: validatePhoneNumber(value) ? "" : t('settings.phoneError'),
      }));
    }
    setFormState(prev => ({ ...prev, [field]: value }));
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) setFormState(prev => ({ ...prev, companyLogo: file }));
  };

  const handleRemoveLogo = () => {
    setFormState(prev => ({ ...prev, companyLogo: "" }));
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const hasLogo = formState.companyLogo &&
    (formState.companyLogo instanceof File || formState.companyLogo.length > 0);

  const logoSrc = formState.companyLogo instanceof File
    ? URL.createObjectURL(formState.companyLogo)
    : formState.companyLogo || picturePlaceHolder;

  const handleSaveChanges = async () => {
    setIsLoading(true);
    setIsModalOpen(true);
    try {
      const result = await updateEOBusinessProfile(user.uid, formState);
      if (result.success) {
        await reload();
        setPopUpModalType("changes success");
        setIsEdit(false);
      } else {
        setPopUpModalType("changes failure");
      }
    } catch (error) {
      console.error("An unexpected error occurred:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setPopUpModalType("cancel edit");
    setIsModalOpen(true);
  };

  const renderEditModal = () => {
    switch (popUpModalType) {
      case "cancel edit":
        return (
          <ModalComponent
            icon={<img src={alert} alt="alert" className="size-20" />}
            title={t('settings.discardChangesTitle')}
            message={t('settings.discardChangesMessage')}
            secondButtonLabel={t('settings.goBack')}
            secondButtonVariant="transparent"
            firstButtonLabel={t('settings.discardChanges')}
            onFirstButtonClick={() => {
              setPopUpModalType(null);
              setIsModalOpen(false);
              setFormState(buildInitialState(userDetails));
              setIsEdit(false);
            }}
            onSecondButtonClick={() => { setPopUpModalType(null); setIsModalOpen(false); }}
          />
        );
      case "changes success":
        return (
          <ModalComponent
            icon={<CheckCircleIconSolid className="size-20 text-Success/700" />}
            title={t('settings.profileChangesSaved')}
            firstButtonLabel={t('common.continue')}
            onFirstButtonClick={() => { setPopUpModalType(null); setIsModalOpen(false); setIsEdit(false); }}
          />
        );
      case "changes failure":
        return (
          <ModalComponent
            icon={<img src={alert} alt="alert" className="size-20" />}
            title={t('settings.profileErrorTitle')}
            message={t('settings.profileErrorMessage')}
            secondButtonLabel={t('settings.goBack')}
            secondButtonVariant="transparent"
            firstButtonLabel={t('settings.tryAgain')}
            onFirstButtonClick={handleSaveChanges}
            onSecondButtonClick={() => { setPopUpModalType(null); setIsModalOpen(false); }}
          />
        );
      default:
        return <ModalLoadingState />;
    }
  };

  return (
    <>
      <PopUpModal open={isModalOpen} handleClose={() => setIsModalOpen(false)} popUpModalType={popUpModalType}>
        {!isLoading ? renderEditModal() : <ModalLoadingState />}
      </PopUpModal>

      <input ref={fileInputRef} type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />

      {!isEdit ? (
        /* ─── VIEW MODE ─────────────────────────────────── */
        <div className="space-y-4 max-w-2xl">
          {/* Profile hero card */}
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            {/* Banner */}
            <div className="h-20 bg-gradient-to-r from-gray-900 via-gray-800 to-gray-700" />
            {/* Avatar + name */}
            <div className="px-6 pb-5">
              <div className="flex items-end justify-between -mt-10 mb-4">
                <div className="relative">
                  <img
                    src={logoSrc}
                    alt="Company logo"
                    className="w-20 h-20 rounded-xl object-cover border-4 border-white shadow-md bg-white"
                  />
                </div>
                <button
                  onClick={() => setIsEdit(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-900 hover:bg-gray-800 text-white text-xs font-semibold rounded-lg transition mb-1"
                >
                  <Pencil className="w-3.5 h-3.5" />
                  {t('settings.editMyProfile')}
                </button>
              </div>
              <h2 className="text-lg font-semibold text-gray-900">
                {formState.businessName || <span className="text-gray-400 font-normal">Company name not set</span>}
              </h2>
              <p className="text-sm text-gray-500 mt-0.5">{formState.userEmail}</p>
              {formState.businessDescription && (
                <p className="mt-3 text-sm text-gray-600 leading-relaxed">{formState.businessDescription}</p>
              )}
            </div>
          </div>

          {/* Contact info card */}
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
              Contact Information
            </h3>
            <InfoRow icon={User} label={t('settings.profileFullName')} value={formState.fullName} />
            <InfoRow icon={Building2} label={t('settings.companyName')} value={formState.businessName} />
            <InfoRow icon={Phone} label={t('settings.businessPhone')} value={formState.contactNumber} />
            <InfoRow icon={Mail} label={t('settings.emailAddress')} value={formState.userEmail} />
            {formState.recoveryEmail && (
              <InfoRow icon={ShieldAlert} label={t('settings.recoveryEmail')} value={formState.recoveryEmail} />
            )}
          </div>

          {/* About card — only if description exists */}
          {formState.businessDescription && (
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                {t('settings.profileAbout')}
              </h3>
              <p className="text-sm text-gray-700 leading-relaxed">{formState.businessDescription}</p>
            </div>
          )}

          {/* Empty state nudge if no data */}
          {!formState.fullName && !formState.contactNumber && (
            <div className="flex items-center gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-700">
              <FileText className="w-4 h-4 flex-shrink-0" />
              Your profile is incomplete. Click <strong>Edit My Profile</strong> to add your details.
            </div>
          )}
        </div>
      ) : (
        /* ─── EDIT MODE ──────────────────────────────────── */
        <div className="space-y-4 max-w-2xl">
          {/* Logo upload card */}
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">
              {t('settings.companyLogo')}
            </h3>
            <div className="flex items-center gap-4">
              <div className="relative group flex-shrink-0">
                <img
                  src={logoSrc}
                  alt="Logo"
                  className="w-20 h-20 rounded-xl object-cover border border-gray-200 bg-gray-50"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="absolute inset-0 bg-black/50 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                >
                  <Camera className="w-5 h-5 text-white" />
                </button>
              </div>
              <div className="flex flex-col gap-2">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                >
                  {hasLogo ? t('settings.changePhoto') : t('settings.uploadPhoto')}
                </button>
                {hasLogo && (
                  <button
                    type="button"
                    onClick={handleRemoveLogo}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-600 hover:text-red-700 transition"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    {t('settings.removePhoto')}
                  </button>
                )}
                <p className="text-xs text-gray-400">{t('settings.logoHint')}</p>
              </div>
            </div>
          </div>

          {/* Fields card */}
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">
              Contact Information
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FieldInput
                label={t('settings.profileFullName')}
                name="fullName"
                value={formState.fullName}
                placeholder={t('settings.profileFullName')}
                onChange={handleFieldChange}
              />
              <FieldInput
                label={t('settings.companyName')}
                name="businessName"
                value={formState.businessName}
                placeholder={t('settings.companyName')}
                onChange={handleFieldChange}
              />
              <FieldInput
                label={t('settings.businessPhone')}
                name="contactNumber"
                value={formState.contactNumber}
                placeholder="+1 234 567 8910"
                error={errors.contactNumber}
                onChange={handleFieldChange}
              />
              <FieldInput
                label={t('settings.emailAddress')}
                name="userEmail"
                value={formState.userEmail}
                placeholder="example@email.com"
                type="email"
                disabled={userSignInMethod === "Google"}
                onChange={handleFieldChange}
              />
              <FieldInput
                label={t('settings.recoveryEmail')}
                name="recoveryEmail"
                value={formState.recoveryEmail}
                placeholder="backup@email.com"
                type="email"
                helperText={t('settings.recoveryEmailHelper')}
                onChange={handleFieldChange}
              />
            </div>
            {userSignInMethod === "Google" && (
              <p className="mt-3 text-xs text-amber-600 flex items-center gap-1.5">
                <ShieldAlert className="w-3.5 h-3.5" />
                {t('settings.googleEmailNote')}
              </p>
            )}
          </div>

          {/* About card */}
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">
              {t('settings.businessDescription')}
            </h3>
            <textarea
              value={formState.businessDescription || ""}
              onChange={e => handleFieldChange("businessDescription", e.target.value)}
              placeholder="Tell vendors what your events are about…"
              rows={4}
              className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg bg-white text-gray-900 placeholder-gray-400
                focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition resize-none"
            />
          </div>

          {/* Action buttons */}
          <div className="flex items-center justify-end gap-3 pt-1">
            <button
              onClick={handleCancelEdit}
              className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={handleSaveChanges}
              className="px-4 py-2 text-sm font-semibold bg-gray-900 hover:bg-gray-800 text-white rounded-lg transition"
            >
              {t('settings.saveChanges')}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default ProfileContent;
